import { css } from '@emotion/react';
import { type FC } from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { ToggleComponent } from '~/_shared/baseComponents/toggle';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { SkeletonWrapperComponent } from '~/_shared/components/skeletonWrapper/skeletonWrapper.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ModalProps } from '~/modal/modalType.enum';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryGroupsListingItemComponent } from './item/boundaryGroupsListingItem.component';
import { BoundaryGroupsListingItemSkeletonComponent } from './item/boundaryGroupsListingItemSkeleton.component';

type BoundaryItem = {
  name: string;
  id: number;
  mapsCount: number;
  boundaryGroupPermissions: BoundaryGroupPermissions;
};

const modalContentStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
  padding: 0,
});

const modalStyle = (theme: Theme) => css({
  background: theme.backgroundColors.secondary,
  // after added to hide the last row's bottom border when the items start touching the bottom of the modal
  '&:after': {
    content: '\'\'',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: 1,
    width: '100%',
    background: theme.backgroundColors.secondary,
  },
});

const contentStyle = css({
  height: '100%',
});

const headingStyle = (theme: Theme) => css({
  padding: '12px 14px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.borderColors.primary}`,
});

const headingTextStyle = css({
  margin: 0,
  fontSize: 14,
  fontWeight: 400,
});

const headingToggleStyle = css({
  marginRight: 24,
});

const noItemsStyle = css({
  margin: 0,
  fontSize: '14px',
  padding: '8px 14px',
});

type BoundaryGroupsListingProps = ModalProps<{
  items: ReadonlyArray<BoundaryItem>;
  isUserTerritoriesEditAllowed: boolean;
  caption: string;
  isLoading: boolean;

  onRemove: (groupId: number) => void;
  onEditName: (groupId: number, newName: string) => void;
  onEditAllowedChange: (allowEditing: boolean) => void;
  onRemoveUnused?: () => void; // internal dev feature for cleaning testing accounts
}>;

export const BoundaryGroupsListingComponent: FC<BoundaryGroupsListingProps> = (props) => {
  const [t] = useTranslation();

  return (
    <ModalComponent
      caption={props.caption}
      css={modalStyle}
      contentStyle={modalContentStyle}
      isOpen={props.isOpen}
      maxWidth={560}
      onClose={props.onClose}
      additionalContent={(props.isLoading && props.items.length > 0) ? <OverlayLoaderComponent /> : null}
      leftFooterContent={props.onRemoveUnused ? (
        <ButtonComponent
          text="Delete Unused" // internal dev feature for cleaning testing accounts
          onClick={props.onRemoveUnused}
          buttonStyle={ButtonStyle.Danger}
        />
      ) : undefined}
    >
      <div css={contentStyle}>
        <div css={headingStyle}>
          <p css={headingTextStyle}>{t('Allow Team Members to Edit My Territories')}</p>
          <ToggleComponent
            css={headingToggleStyle}
            isOn={props.isUserTerritoriesEditAllowed}
            onChange={props.onEditAllowedChange}
          />
        </div>

        {props.isLoading && props.items.length === 0 && (
          <SkeletonWrapperComponent
            animationDurationMs={2000}
            itemsCount={5}
            renderItem={({ durationMs, delayMs }) => (
              <BoundaryGroupsListingItemSkeletonComponent
                animationDurationMs={durationMs}
                animationDelayMs={delayMs}
              />
            )}
          />
        )}

        {props.items.map((item, index) => (
          <BoundaryGroupsListingItemComponent
            boundaryGroupId={item.id}
            boundaryGroupPermissions={item.boundaryGroupPermissions}
            index={index}
            isLoading={props.isLoading}
            key={item.id}
            mapsCount={item.mapsCount}
            name={item.name}
            itemId={item.id}
            onEditName={props.onEditName}
            onRemove={props.onRemove}
          />
        ))}

        {(!props.isLoading && props.items.length === 0) &&
          <p css={noItemsStyle}>{t('No territory groups')}</p>
        }
      </div>
    </ModalComponent>
  );
};
